'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Box, IconButton, Slider, Typography, useTheme, CircularProgress } from '@mui/material';
import { Pause as PauseIcon, PlayArrow as PlayArrowIcon, Forward10 as Forward10Icon, Replay10 as Replay10Icon, VolumeUp as VolumeUpIcon, ErrorOutline } from '@mui/icons-material';
// Assuming NewStoryPlayer is primarily UI, we might integrate parts or use MUI directly
// import NewStoryPlayer from '../StoryPlayer/NewStoryPlayer'; // Path might need adjustment
import * as toastHook from '../../hooks/use-toast.jsx';
import { generateAudio, uploadAudioToStorage } from '../../services/ttsService.js';


const AVERAGE_WPM = 180; // Words per minute for estimation

// Helper function to format time (MM:SS)
const formatTime = (seconds) => {
  if (isNaN(seconds) || !isFinite(seconds)) {
    return "00:00";
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};


export default function StoryAudioPlayer({ storyText, voice, storyId, storedAudioUrl, onAudioStored }) {
  const theme = useTheme();
  const { toast } = toastHook.useToast();
  const { getToken } = useAuth();
  const { user } = useUser();

  // --- State for Audio Player ---
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Keep internal pause state if needed
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0); // Progress percentage (0-100)
  const [audioSrc, setAudioSrc] = useState(storedAudioUrl || null);

  const audioElementRef = useRef(null);
  const currentStoryTextRef = useRef(storedAudioUrl ? storyText : null);

  // If a stored URL is provided (or swaps in later), use it directly — no TTS call needed.
  useEffect(() => {
    if (storedAudioUrl) {
      setAudioSrc(storedAudioUrl);
      currentStoryTextRef.current = storyText;
    }
  }, [storedAudioUrl, storyText]);

  // --- Cleanup Object URL --- 
  useEffect(() => {
    // Revoke object URL when component unmounts or audio source changes
    const currentAudioSrc = audioSrc;
    return () => {
      if (currentAudioSrc && currentAudioSrc.startsWith('blob:')) {
        URL.revokeObjectURL(currentAudioSrc);
      }
    };
  }, [audioSrc]);

  // --- Setup Audio Element Listeners --- 
  useEffect(() => {
    const audio = audioElementRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      // Reset time/progress if needed when new src is loaded
      setCurrentTime(audio.currentTime);
      setProgress(duration > 0 ? (audio.currentTime / audio.duration) * 100 : 0);
    };

    const handleTimeUpdate = () => {
      const current = audio.currentTime;
      const total = audio.duration;
      setCurrentTime(current);
      setProgress(total > 0 ? (current / total) * 100 : 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(duration); // Set to end
      setProgress(100);
      // Optional: Reset to beginning after a short delay
      // setTimeout(() => {
      //   setCurrentTime(0);
      //   setProgress(0);
      // }, 1500);
    };

    const handleError = (e) => {
      console.error("Audio Element Error:", e);
      setError("Error playing audio.");
      toast({ title: "Audio Error", description: "Could not play the audio file.", variant: "destructive" });
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Cleanup listeners
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [duration]); // Re-attach if duration changes (e.g., new src)

  // --- Fetch Audio Function --- 
  const fetchAndSetAudio = useCallback(async (textToPlay) => {
    if (!textToPlay || textToPlay.trim().length === 0) {
      setError("No text provided to generate audio.");
      return false;
    }

    setIsLoading(true);
    setError(null);
    // Reset player state before fetching new audio
    if (audioElementRef.current) audioElementRef.current.pause();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTime(0);
    setDuration(0);
    setProgress(0);
    setAudioSrc(null); // Revokes old blob url via useEffect cleanup
    currentStoryTextRef.current = textToPlay; // Store the text we're fetching for

    try {
      const authToken = await getToken();
      const audioContent = await generateAudio(textToPlay, voice, authToken);
      const audioBlob = new Blob([audioContent], { type: 'audio/wav' });
      const objectUrl = URL.createObjectURL(audioBlob);
      setAudioSrc(objectUrl);

      // Fire-and-forget: persist to Firebase Storage so future replays skip TTS.
      // Only do this if we know which story it belongs to.
      if (storyId && voice) {
          uploadAudioToStorage(audioContent, storyId, voice, user?.id, null, authToken)
          .then((downloadUrl) => {
            if (onAudioStored) onAudioStored(voice, downloadUrl);
          })
          .catch((uploadErr) => {
            // Non-fatal — audio still plays from the blob URL this session.
            console.warn('Failed to persist audio to storage:', uploadErr.message);
          });
      }

      // Ensure the audio element exists before proceeding
      if (audioElementRef.current) {
         audioElementRef.current.load(); // Important: Load the new source
         return true; // Indicate success
      } else {
        setError("Audio element not available.");
        return false; // Indicate failure
      }
    } catch (err) {
      setError(err.message || "Failed to fetch audio.");
      toast({ title: "TTS Error", description: err.message || "Could not generate audio.", variant: "destructive" });
      currentStoryTextRef.current = null; // Clear ref on error
      return false; // Indicate failure
    } finally {
      setIsLoading(false);
    }
  }, [toast, voice, storyId, user?.id, onAudioStored, getToken]);

  // --- Control Functions ---

  const handleToggleSpeech = useCallback(async () => {
    const audio = audioElementRef.current;
    if (!audio) {
      setError("Audio player not ready.");
      return;
    }

    // Prevent clicks while loading
    if (isLoading) {
      return;
    }

    // --- Define the play function ---
    const tryPlayAudio = async () => {
      try {
        await audio.play();
        setIsPlaying(true);
        setIsPaused(false);
        setError(null); // Clear any previous "auto-play blocked" messages
      } catch (playError) {
        console.error("Play failed:", playError);
        // Keep the specific error message from your screenshot
        setError("Auto-play blocked. Click play to start.");
        setIsPlaying(false);
        setIsPaused(false);
      }
    };

    // --- Main Logic ---

    // Case 1: No audio loaded OR text changed -> Fetch new audio then set up play listener
    if (!audioSrc || currentStoryTextRef.current !== storyText) {
      setIsLoading(true); // Set loading before fetch
      const success = await fetchAndSetAudio(storyText);
      setIsLoading(false); // Unset loading after fetch attempt
      
      if (success && audioElementRef.current) {
         // Audio source is set, now wait for readiness

         // Clear any previous listener
         audioElementRef.current.oncanplaythrough = null;

         // Attach the listener
         audioElementRef.current.oncanplaythrough = () => {
           tryPlayAudio();
           // Remove the listener after it fires
           if (audioElementRef.current) {
               audioElementRef.current.oncanplaythrough = null;
           }
         };

         // Fallback check: If already ready, play immediately
         if (audioElementRef.current.readyState >= 4) { // HAVE_ENOUGH_DATA
           // Still remove listener in case it was somehow attached
            audioElementRef.current.oncanplaythrough = null;
           tryPlayAudio();
         }

      } else if (!success) {
        // Fetch failed, error state is set within fetchAndSetAudio
      }
      return; 
    }

    // Case 2: Audio is loaded, toggle play/pause
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      setIsPaused(true);
    } else {
      // If paused or stopped, try playing again
      tryPlayAudio();
    }
  }, [audioSrc, isPlaying, isLoading, storyText, fetchAndSetAudio, toast, voice]);

  const handleSeek = useCallback((event, newValue) => {
    if (!audioElementRef.current || duration === 0) return;
    const newProgress = typeof newValue === 'number' ? newValue : progress;
    const newTime = (newProgress / 100) * duration;
    audioElementRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(newProgress);
  }, [duration, progress]);

  const handleSkipBackward = useCallback(() => {
    if (!audioElementRef.current || duration === 0) return;
    const newTime = Math.max(0, audioElementRef.current.currentTime - 10);
    audioElementRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress((newTime / duration) * 100);
  }, [duration]);

  const handleSkipForward = useCallback(() => {
    if (!audioElementRef.current || duration === 0) return;
    const newTime = Math.min(duration, audioElementRef.current.currentTime + 10);
    audioElementRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress((newTime / duration) * 100);
  }, [duration]);

  // --- Reset audio when storyText changes --- 
  useEffect(() => {
    // Don't reset if it's the initial load or text is empty
    if (currentStoryTextRef.current === null && !storyText) {
        return;
    }
    
    // If text changes from what the current audio represents, reset the player
    if (storyText !== currentStoryTextRef.current) {
       if (audioElementRef.current) {
         audioElementRef.current.pause();
       }
       setIsPlaying(false);
       setIsPaused(false);
       setCurrentTime(0);
       setDuration(0);
       setProgress(0);
       setAudioSrc(null); // This will trigger cleanup of the old blob URL
       setError(null);
       setIsLoading(false);
       currentStoryTextRef.current = null; // Reset ref until new audio is fetched
    }
  }, [storyText]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Audio Element (Hidden) */}
      <audio ref={audioElementRef} src={audioSrc || undefined} preload="metadata"></audio>

      {/* Player Controls Section */}
      <Box sx={{ px: 1, mb: 2 }}>
        <Slider
          aria-label="Story progress"
          value={progress}
          onChange={handleSeek} // Seek on drag is fine
          // onChangeCommitted={handleSliderChangeCommitted} // Not needed for basic seek
          min={0}
          max={100}
          step={0.1}
          disabled={isLoading || !audioSrc || duration === 0}
          sx={{ 
            height: 6, 
            color: theme.palette.primary.main,
            padding: '10px 0', 
            '& .MuiSlider-track': { border: 'none' },
            '& .MuiSlider-rail': { opacity: 0.3, bgcolor: 'grey.400' },
            '& .MuiSlider-thumb': {
                height: 14, width: 14, bgcolor: '#fff',
                border: `2px solid ${theme.palette.primary.main}`,
                '&::before': { display: 'none' },
                '&:hover, &.Mui-focusVisible': { boxShadow: 'inherit' },
                '&.Mui-active': { boxShadow: 'inherit' }
            },
          }}
        />
        {/* Controls Row */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 0.5
        }}>
          {/* Current Time */}
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: '40px' }}>
            {formatTime(currentTime)}
          </Typography>
          {/* Central Buttons */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5
          }}>
            <IconButton aria-label="Skip backward 10 seconds" onClick={handleSkipBackward} size="small" sx={{ color: 'text.primary' }} disabled={isLoading || !audioSrc || duration === 0}>
              <Replay10Icon />
            </IconButton>
            <IconButton
              aria-label={isPlaying ? "Pause story" : "Play story"}
              aria-pressed={isPlaying}
              aria-busy={isLoading}
              onClick={handleToggleSpeech}
              size="large"
              disabled={!storyText || isLoading} // Disable if no text or loading
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                width: 50,
                height: 50,
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : (isPlaying ? <PauseIcon fontSize="medium" /> : <PlayArrowIcon fontSize="medium" sx={{ ml: 0.25 }}/>)}
            </IconButton>
            <IconButton aria-label="Skip forward 10 seconds" onClick={handleSkipForward} size="small" sx={{ color: 'text.primary' }} disabled={isLoading || !audioSrc || duration === 0}>
              <Forward10Icon />
            </IconButton>
          </Box>
          {/* Total Duration */}
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: '40px' }}>
            {formatTime(duration)}
          </Typography>
        </Box>
      </Box>
      {/* End Player Controls Section */}

      {/* Display Error Message */}
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main', mt: 1, gap: 0.5 }}>
          <ErrorOutline fontSize="small" />
          <Typography variant="caption" color="inherit" role="alert">
            {error}
          </Typography>
        </Box>
      )}

    </Box>
  );
}
